import React, { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { analytics } from '../utils/analytics';
import { checkCookieSupport, AUTH_ERROR_MESSAGES, classifyAuthError } from '../utils/authDiagnostics';
import { Eye, EyeOff, Mail, Lock, User, ArrowRight, AlertTriangle, MailCheck, Sparkles } from 'lucide-react';
import { API } from '../utils/apiBase';
import Logo from '../components/Logo';

// Error formatting helper
const formatApiErrorDetail = (detail) => {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

const RegisterPage = () => {
  const [mode, setMode] = useState('password'); // 'password' | 'magic'
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [cookieWarning, setCookieWarning] = useState('');
  const [loading, setLoading] = useState(false);
  // Magic-link signup state
  const [magicLoading, setMagicLoading] = useState(false);
  const [magicSentFor, setMagicSentFor] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const { register, googleLogin, refreshAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Conversion magic: if the user came from the Free ATS job-match cards,
  // they signed up with intent. Drop them directly into the dashboard with
  // the right job preselected — zero-friction value moment.
  const postSignupRedirect = (() => {
    const intent = searchParams.get('intent');
    const jobId = searchParams.get('job_id');
    if (intent === 'tailor' && jobId) {
      return `/dashboard?intent=tailor&job_id=${encodeURIComponent(jobId)}`;
    }
    return '/dashboard';
  })();

  useEffect(() => {
    const cookieCheck = checkCookieSupport();
    if (!cookieCheck.ok) {
      setCookieWarning(AUTH_ERROR_MESSAGES[cookieCheck.reason] || AUTH_ERROR_MESSAGES.cookies_blocked);
    }
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await register(email, password, name);
      analytics.signUp('email');
      navigate(postSignupRedirect);
    } catch (err) {
      const reason = classifyAuthError(err);
      setError(
        (reason && AUTH_ERROR_MESSAGES[reason]) ||
          formatApiErrorDetail(err.response?.data?.detail) ||
          err.message
      );
    } finally {
      setLoading(false);
    }
  }, [name, email, password, register, navigate, postSignupRedirect]);

  const togglePassword = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  // ---- Magic-link signup flow --------------------------------------------
  const handleMagicSignup = useCallback(async (e) => {
    e.preventDefault();
    setError('');
    if (name.trim().length < 2) {
      setError('Please enter your full name.');
      return;
    }
    if (!email || !/.+@.+\..+/.test(email)) {
      setError('Enter a valid email address.');
      return;
    }
    setMagicLoading(true);
    try {
      await axios.post(
        `${API}/auth/magic-link/signup`,
        { email: email.trim().toLowerCase(), name: name.trim() },
        { withCredentials: true },
      );
      setMagicSentFor(email.trim().toLowerCase());
      analytics.signUp('magic_link');
    } catch (err) {
      const reason = classifyAuthError(err);
      setError(
        (reason && AUTH_ERROR_MESSAGES[reason]) ||
          formatApiErrorDetail(err.response?.data?.detail) ||
          err.message,
      );
    } finally {
      setMagicLoading(false);
    }
  }, [name, email]);

  const handleOtpVerify = useCallback(async (e) => {
    e?.preventDefault?.();
    setError('');
    const cleaned = otpCode.replace(/\D/g, '');
    if (cleaned.length !== 6) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    setOtpLoading(true);
    try {
      await axios.post(
        `${API}/auth/otp/verify`,
        { email: magicSentFor, code: cleaned },
        { withCredentials: true },
      );
      await refreshAuth?.();
      navigate(postSignupRedirect);
    } catch (err) {
      setError(
        formatApiErrorDetail(err.response?.data?.detail) ||
          'That code didn\u2019t work. Try again or request a new one.',
      );
    } finally {
      setOtpLoading(false);
    }
  }, [otpCode, magicSentFor, navigate, refreshAuth, postSignupRedirect]);

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex flex-1 bg-[#0f1115] items-center justify-center p-12">
        <div className="max-w-md text-white">
          <h2 className="font-satoshi text-4xl font-black tracking-tight mb-6">
            Start your journey to more interviews
          </h2>
          <p className="text-[#9ca3af] text-lg leading-relaxed">
            Create your free account and get instant access to AI-powered resume optimization.
          </p>
          <div className="mt-12 grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-3xl font-satoshi font-black text-[#10b981]">90%</div>
              <div className="text-sm text-[#6b7280] mt-1">Use ATS</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-satoshi font-black text-[#eab308]">3x</div>
              <div className="text-sm text-[#6b7280] mt-1">More Interviews</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-satoshi font-black text-[#002fa7]">10K+</div>
              <div className="text-sm text-[#6b7280] mt-1">Users</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-20">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <Link to="/" aria-label="Ascend — Home" className="inline-block mb-6">
              <Logo size={56} priority />
            </Link>
            <h1 className="font-satoshi text-3xl font-black tracking-tight text-[#0f1115]">
              Create your account
            </h1>
            <p className="mt-2 text-[#4b5563]">
              Get started with 1 free resume optimization per month
            </p>
          </div>

          {cookieWarning && (
            <div
              className="mb-4 p-4 bg-[#fffbeb] border border-[#eab308] text-[#92400e] text-sm flex items-start gap-3"
              data-testid="register-cookie-warning"
            >
              <AlertTriangle size={18} className="shrink-0 mt-0.5" />
              <div>
                <strong className="font-semibold">Cookies are blocked on this browser.</strong>
                <div className="mt-1">{cookieWarning}</div>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-[#fef2f2] border border-[#e11d48] text-[#e11d48] text-sm" data-testid="register-error">
              {error}
            </div>
          )}

          {/* Mode toggle: Password vs Magic Link */}
          {!magicSentFor && (
            <div className="flex mb-6 border border-[#e5e7eb] bg-[#f4f5f7] p-1" data-testid="signup-mode-toggle">
              <button
                type="button"
                onClick={() => { setMode('password'); setError(''); }}
                className={`flex-1 py-2 text-sm font-medium transition-colors ${
                  mode === 'password'
                    ? 'bg-white text-[#0f1115] shadow-sm'
                    : 'text-[#6b7280] hover:text-[#0f1115]'
                }`}
                data-testid="mode-password"
              >
                Password
              </button>
              <button
                type="button"
                onClick={() => { setMode('magic'); setError(''); }}
                className={`flex-1 py-2 text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                  mode === 'magic'
                    ? 'bg-white text-[#0f1115] shadow-sm'
                    : 'text-[#6b7280] hover:text-[#0f1115]'
                }`}
                data-testid="mode-magic"
              >
                <Sparkles size={14} />
                Email link (no password)
              </button>
            </div>
          )}

          {/* "Check your inbox" confirmation screen (magic-link sent) */}
          {magicSentFor ? (
            <div className="space-y-6" data-testid="magic-sent-screen">
              <div className="p-5 bg-[#ecfdf5] border border-[#10b981] flex items-start gap-3">
                <MailCheck size={22} className="text-[#10b981] shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-[#065f46]">Account created! Check your email.</div>
                  <div className="mt-1 text-sm text-[#065f46]">
                    We sent a sign-in link and a 6-digit code to <strong>{magicSentFor}</strong>. Click the link, or enter the code below.
                  </div>
                </div>
              </div>

              <form onSubmit={handleOtpVerify} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#0f1115] mb-2">6-digit code</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value)}
                    onPaste={(e) => {
                      const raw = (e.clipboardData || window.clipboardData).getData('text') || '';
                      const digits = raw.replace(/\D/g, '').slice(0, 6);
                      if (digits) {
                        e.preventDefault();
                        setOtpCode(digits);
                      }
                    }}
                    className="w-full px-4 py-3 border border-[#e5e7eb] bg-white text-[#0f1115] text-center tracking-[0.5em] text-xl font-mono focus:border-[#002fa7] transition-colors"
                    placeholder="123456"
                    required
                    data-testid="signup-otp-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={otpLoading}
                  className="w-full btn-primary flex items-center justify-center space-x-2 disabled:opacity-50"
                  data-testid="signup-otp-verify"
                >
                  <span>{otpLoading ? 'Verifying...' : 'Verify & continue'}</span>
                  {!otpLoading && <ArrowRight size={20} />}
                </button>
                <button
                  type="button"
                  onClick={() => { setMagicSentFor(''); setOtpCode(''); setError(''); }}
                  className="w-full text-sm text-[#6b7280] hover:text-[#0f1115]"
                  data-testid="signup-otp-cancel"
                >
                  Use a different email
                </button>
              </form>
            </div>
          ) : mode === 'password' ? (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-[#0f1115] mb-2">
                Full Name
              </label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6b7280]" size={20} />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-[#e5e7eb] bg-white text-[#0f1115] focus:border-[#002fa7] transition-colors"
                  placeholder="John Doe"
                  required
                  data-testid="register-name"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#0f1115] mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6b7280]" size={20} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-[#e5e7eb] bg-white text-[#0f1115] focus:border-[#002fa7] transition-colors"
                  placeholder="you@example.com"
                  required
                  data-testid="register-email"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#0f1115] mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6b7280]" size={20} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-12 py-3 border border-[#e5e7eb] bg-white text-[#0f1115] focus:border-[#002fa7] transition-colors"
                  placeholder="Min. 6 characters"
                  required
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={togglePassword}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#6b7280] hover:text-[#0f1115]"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="register-submit"
            >
              <span>{loading ? 'Creating account...' : 'Create Account'}</span>
              {!loading && <ArrowRight size={20} />}
            </button>
          </form>
          ) : (
            /* Magic-link signup form (no password) */
            <form onSubmit={handleMagicSignup} className="space-y-6" data-testid="magic-signup-form">
              <div>
                <label className="block text-sm font-medium text-[#0f1115] mb-2">Full Name</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6b7280]" size={20} />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border border-[#e5e7eb] bg-white text-[#0f1115] focus:border-[#002fa7] transition-colors"
                    placeholder="John Doe"
                    required
                    data-testid="magic-signup-name"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0f1115] mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6b7280]" size={20} />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border border-[#e5e7eb] bg-white text-[#0f1115] focus:border-[#002fa7] transition-colors"
                    placeholder="you@example.com"
                    required
                    data-testid="magic-signup-email"
                  />
                </div>
              </div>
              <div className="p-3 bg-[#eff6ff] border border-[#002fa7]/20 text-xs text-[#1e3a8a] leading-relaxed">
                <strong>How it works:</strong> We&rsquo;ll email you a secure sign-in link plus a 6-digit code. No password to remember &mdash; just enter the code here or click the link in your inbox.
              </div>
              <button
                type="submit"
                disabled={magicLoading}
                className="w-full btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="magic-signup-submit"
              >
                <Sparkles size={18} />
                <span>{magicLoading ? 'Sending link...' : 'Send me a sign-in link'}</span>
              </button>
            </form>
          )}

          {!magicSentFor && (
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#e5e7eb]"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-[#6b7280]">Or continue with</span>
              </div>
            </div>

            <button
              onClick={() => { analytics.signUp('google'); googleLogin(); }}
              className="mt-6 w-full flex items-center justify-center space-x-3 py-3 border border-[#e5e7eb] bg-white hover:bg-[#f4f5f7] transition-colors"
              data-testid="google-register"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span className="text-[#0f1115] font-medium">Continue with Google</span>
            </button>
          </div>
          )}

          <p className="mt-8 text-center text-[#6b7280]">
            Already have an account?{' '}
            <Link to="/login" className="text-[#002fa7] font-medium hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </p>

          <p className="mt-4 text-center text-xs text-[#6b7280]">
            By creating an account, you agree to our{' '}
            <Link to="/terms" className="text-[#002fa7] hover:underline">Terms of Service</Link>
            {' '}and{' '}
            <Link to="/privacy" className="text-[#002fa7] hover:underline">Privacy Policy</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
